import { toUserMessage } from '../../lib/errors.js';
import { shareWithNavigator } from '../../lib/adapters/share/index.js';
import {
  IonAlert,
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
  IonNote,
  IonPage,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToast,
  IonToolbar,
} from '@ionic/react';
import { add, businessOutline, closeOutline, copyOutline, keyOutline, printOutline, qrCodeOutline, removeCircleOutline, trashOutline } from 'ionicons/icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Destination, DestinationKind, DestinationWard } from '@trackeat/shared';
import { DESTINATION_KINDS, DESTINATION_KIND_LABELS } from '@trackeat/shared';
import { useAuth } from '../../lib/auth/AuthProvider.js';
import { useZoneBusinesses } from '../../lib/auth/useZoneBusinesses.js';
import {
  useCollettivaEnabled,
  useEnqueueMaster,
  useMasters,
  useTenantAccounts,
  useTransportDocumentsAccessGate,
} from '../../lib/data/queries.js';
import { getSupabase } from '../../lib/supabase.js';
import { getSyncEngine } from '../../lib/sync/engine.js';

// Password leggibile generata per l'account preposto (niente caratteri ambigui).
function genPassword(): string {
  const chars = 'abcdefghijkmnpqrstuvwxyz23456789';
  const buf = new Uint32Array(8);
  crypto.getRandomValues(buf);
  let s = '';
  for (let i = 0; i < 8; i++) s += chars[buf[i] % chars.length];
  return `tk-${s}`;
}

// T-438 — token QR del reparto: lungo, random, non indovinabile (32 char base32).
function genWardToken(): string {
  const chars = 'abcdefghijkmnpqrstuvwxyz0123456789';
  const buf = new Uint32Array(32);
  crypto.getRandomValues(buf);
  let s = '';
  for (let i = 0; i < 32; i++) s += chars[buf[i] % chars.length];
  return s;
}
// PIN reparto: 4 cifre (facile da digitare, cambiabile dall'owner).
function genWardPin(): string {
  const buf = new Uint32Array(4);
  crypto.getRandomValues(buf);
  return Array.from(buf, (n) => String(n % 10)).join('');
}
// URL pubblico del QR (pagina pubblica per-token servita da Cloudflare — T-438 fase 2).
// NB: `/r/` è già usato (redirect tracciati) → il path è `/reparto/`.
const wardOrderUrl = (token: string): string => `https://trackeat.eu/reparto/${token}`;
// Genera il PNG data-URL del QR (libreria qrcode, import dinamico come in preview.ts).
async function qrPng(text: string): Promise<string> {
  try {
    const mod = await import('qrcode');
    const lib = (mod as unknown as { default?: { toDataURL(t: string, o?: unknown): Promise<string> } }).default
      ?? (mod as unknown as { toDataURL(t: string, o?: unknown): Promise<string> });
    return await lib.toDataURL(text, { width: 320, margin: 1, errorCorrectionLevel: 'M' });
  } catch {
    return '';
  }
}

// T-215 (collettiva): anagrafica destinazioni dei pasti veicolati.
// Le righe in cache locale ottimistica possono non avere ancora i campi
// riempiti dal server (version/created_at/archived_at) — li trattiamo come
// opzionali qui, senza toccare lo schema condiviso.
type DestinationRow = Omit<Destination, 'version' | 'created_at' | 'archived_at'> & {
  version?: number;
  created_at?: string;
  archived_at?: string | null;
};

const KIND_BADGE_COLORS: Record<DestinationKind, { bg: string; fg: string }> = {
  bar_caffetteria: { bg: '#fef3c7', fg: '#92400e' },
  pasticceria_gelateria: { bg: '#fce7f3', fg: '#9d174d' },
  panificio_gastronomia: { bg: '#ffedd5', fg: '#9a3412' },
  ristorante_pizzeria: { bg: '#dcfce7', fg: '#166534' },
  hotel_bnb: { bg: '#e0e7ff', fg: '#3730a3' },
  negozio_rivendita: { bg: '#dbeafe', fg: '#1d4ed8' },
  supermercato_gdo: { bg: '#ccfbf1', fg: '#0f766e' },
  catering_eventi: { bg: '#f3e8ff', fg: '#7e22ce' },
  grossista_distributore: { bg: '#e2e8f0', fg: '#334155' },
  scuola: { bg: '#e0f2fe', fg: '#0369a1' },
  ospedale: { bg: '#fee2e2', fg: '#b91c1c' },
  rsa: { bg: '#fef3c7', fg: '#b45309' },
  azienda: { bg: '#ede9fe', fg: '#6d28d9' },
  altro: { bg: '#f1f5f9', fg: '#475569' },
};

interface DestinationForm {
  name: string;
  kind: DestinationKind;
  address: string;
  city: string;
  phone: string;
  email: string;
  contact_name: string;
  notes: string;
  // T-421: reparti (opzionali) + modello ordine (sottoinsieme catalogo; null = tutto).
  wards: DestinationWard[];
  orderModel: string[] | null;
}

const EMPTY_FORM: DestinationForm = {
  name: '',
  kind: 'scuola',
  address: '',
  city: '',
  phone: '',
  email: '',
  contact_name: '',
  notes: '',
  wards: [],
  orderModel: null,
};

interface CatalogRow { category: string; label: string; sort_order: number; }
const CATALOG_CAT_ORDER = ['PRIMI', 'SECONDI', 'CONTORNI', 'FRUTTA'];

export function DestinationsPage(): JSX.Element {
  return <DestinationsPageContent variant="collettiva" />;
}

export function TransportRecipientsPage(): JSX.Element {
  const access = useTransportDocumentsAccessGate();

  if (access.checking) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
            <IonSpinner name="crescent" aria-label="Verifica accesso" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!access.enabled) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start"><IonBackButton defaultHref="/documents/transport" /></IonButtons>
            <IonTitle>Clienti e destinatari</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 14, padding: 16, color: '#9a3412' }}>
            Modulo non attivo o accesso non verificabile. Chiedi al consulente di
            abilitarlo per questo ristorante.
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return <DestinationsPageContent variant="transport" />;
}

function DestinationsPageContent({ variant }: { variant: 'collettiva' | 'transport' }): JSX.Element {
  const isTransportRecipients = variant === 'transport';
  const { profile } = useAuth();
  const collettivaEnabled = useCollettivaEnabled();
  // T-403: anagrafica scoped sulla ZONA attiva (sedi con la stessa città).
  const { zoneIds, zoneName } = useZoneBusinesses();
  const { data: destinations = [] } = useMasters<DestinationRow>('destinations');
  const enqueue = useEnqueueMaster();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<DestinationRow | null>(null);
  const [form, setForm] = useState<DestinationForm>(EMPTY_FORM);
  const [confirmArchive, setConfirmArchive] = useState<DestinationRow | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // T-306 follow-up: account "preposto" per destinazione (creazione inline).
  const qc = useQueryClient();
  const isAdmin = profile?.role === 'consulente' || profile?.role === 'ristoratore' || profile?.role === 'osa';
  const { data: accounts = [] } = useTenantAccounts(isAdmin && !isTransportRecipients);
  const accountByDest = useMemo(() => {
    const m = new Map<string, string>(); // destination_id → email
    for (const a of accounts) if (a.kind === 'preposto' && a.destination_id) m.set(a.destination_id, a.email);
    return m;
  }, [accounts]);
  const [prepForm, setPrepForm] = useState({ email: '', password: '' });
  const [prepFormOpen, setPrepFormOpen] = useState(false);
  // T-438: reset password di un preposto già esistente.
  const [resetPw, setResetPw] = useState<string | null>(null);
  const [prepBusy, setPrepBusy] = useState(false);
  const [prepResult, setPrepResult] = useState<{ email: string; password: string } | null>(null);

  // T-421: catalogo righe ordinabili (online-diretto, come nel form del preposto)
  // per l'assegnazione del MODELLO alla destinazione.
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  useEffect(() => {
    if (isTransportRecipients || !editorOpen || !profile?.active_tenant_id) return;
    let cancelled = false;
    void (async () => {
      const { data } = await getSupabase()
        .from('order_catalog_rows')
        .select('category, label, sort_order')
        .eq('tenant_id', profile.active_tenant_id!)
        .eq('active', true)
        .is('archived_at', null)
        .order('sort_order');
      if (!cancelled && data) setCatalog(data as CatalogRow[]);
    })();
    return () => { cancelled = true; };
  }, [editorOpen, isTransportRecipients, profile?.active_tenant_id]);
  const catalogByCat = useMemo(() => {
    const m = new Map<string, CatalogRow[]>();
    for (const r of catalog) { const a = m.get(r.category) ?? []; a.push(r); m.set(r.category, a); }
    return m;
  }, [catalog]);
  const [newWard, setNewWard] = useState('');
  // T-438: PNG del QR per token reparto (generati async, cache in state).
  const [qrPngs, setQrPngs] = useState<Record<string, string>>({});
  // T-438 fix — stampa A4 QR IN-APP (iframe modale), non window.open (che su app
  // nativa apriva una webview bianca che si incastrava, senza indietro né stampa).
  const [printHtml, setPrintHtml] = useState<string | null>(null);
  const printRef = useRef<HTMLIFrameElement>(null);
  useEffect(() => {
    if (isTransportRecipients) return;
    let cancelled = false;
    void (async () => {
      for (const w of form.wards) {
        const tok = w.token ?? '';
        if (tok && !qrPngs[tok]) {
          const png = await qrPng(wardOrderUrl(tok));
          if (cancelled) return;
          if (png) setQrPngs((m) => ({ ...m, [tok]: png }));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [form.wards, isTransportRecipients, qrPngs]);

  const visible = useMemo(
    () =>
      destinations
        .filter((d) => (
          !d.archived_at
          && (isTransportRecipients
            ? d.business_id == null
            : (!zoneIds || (d.business_id != null && zoneIds.includes(d.business_id))))
        ))
        .sort((a, b) => a.name.localeCompare(b.name, 'it')),
    [destinations, isTransportRecipients, zoneIds],
  );

  function resetPrep(): void {
    setPrepFormOpen(false);
    setPrepResult(null);
    setPrepBusy(false);
  }

  function openNew(): void {
    setEditing(null);
    setForm(isTransportRecipients ? { ...EMPTY_FORM, kind: 'altro' } : EMPTY_FORM);
    setNewWard('');
    resetPrep();
    setEditorOpen(true);
  }

  function openEdit(d: DestinationRow): void {
    setEditing(d);
    setForm({
      name: d.name,
      kind: d.kind,
      address: d.address ?? '',
      city: d.city ?? '',
      phone: d.phone ?? '',
      email: d.email ?? '',
      contact_name: d.contact_name ?? '',
      notes: d.notes ?? '',
      wards: (d.wards ?? []).slice().sort((a, b) => a.sort_order - b.sort_order),
      orderModel: d.order_model ?? null,
    });
    setNewWard('');
    resetPrep();
    setEditorOpen(true);
  }

  async function onSave(): Promise<void> {
    if (!profile?.active_tenant_id) {
      setToast('Nessun tenant attivo');
      return;
    }
    const name = form.name.trim();
    if (!name) {
      setToast(isTransportRecipients ? 'Indica il nome del cliente/destinatario' : 'Indica il nome della destinazione');
      return;
    }
    const email = form.email.trim();
    if (email && !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      setToast('Email non valida');
      return;
    }
    // T-421: modello vuoto = null (= catalogo completo, retro-compat); reparti
    // ri-ordinati e ripuliti dei nomi vuoti.
    const wardsClean = form.wards
      // T-438: preserva token + pin del QR reparto (altrimenti l'accesso si perde al salva).
      .map((w, i) => ({ id: w.id, name: w.name.trim(), sort_order: i, token: w.token ?? null, pin: w.pin ?? null }))
      .filter((w) => w.name.length > 0);
    const modelClean = form.orderModel && form.orderModel.length > 0 ? form.orderModel : null;
    const fields = {
      name,
      kind: form.kind,
      address: form.address.trim() || null,
      city: form.city.trim() || null,
      phone: form.phone.trim() || null,
      email: email || null,
      contact_name: form.contact_name.trim() || null,
      notes: form.notes.trim() || null,
      wards: wardsClean,
      order_model: modelClean,
    };
    try {
      if (editing) {
        await enqueue.mutateAsync({
          table: 'destinations',
          id: editing.id,
          op: 'update',
          base_version: editing.version ?? 0,
          patch: { id: editing.id, tenant_id: editing.tenant_id, ...fields },
        });
        setToast(isTransportRecipients ? 'Cliente/destinatario aggiornato' : 'Destinazione aggiornata');
        setEditorOpen(false);
      } else {
        const id = crypto.randomUUID();
        const businessId = isTransportRecipients ? null : (profile.active_business_id ?? null);
        await enqueue.mutateAsync({
          table: 'destinations',
          id,
          op: 'insert',
          base_version: 0,
          patch: {
            id,
            tenant_id: profile.active_tenant_id,
            business_id: businessId,
            ...fields,
          },
        });
        if (isTransportRecipients) {
          setToast('Cliente/destinatario creato');
          setEditorOpen(false);
          return;
        }
        // Resto nel modale in modalità MODIFICA della destinazione appena creata,
        // così la sezione "Account di accesso" diventa subito disponibile.
        setEditing({
          id,
          tenant_id: profile.active_tenant_id,
          business_id: businessId,
          version: 0,
          ...fields,
        } as DestinationRow);
        resetPrep();
        setToast('Destinazione creata — ora puoi creare l’account di accesso');
      }
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function archive(d: DestinationRow): Promise<void> {
    try {
      await enqueue.mutateAsync({
        table: 'destinations',
        id: d.id,
        op: 'archive',
        base_version: d.version ?? 0,
        patch: { id: d.id },
      });
      setToast(isTransportRecipients ? 'Cliente/destinatario archiviato' : 'Destinazione archiviata');
      setConfirmArchive(null);
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }

  async function createPreposto(): Promise<void> {
    if (!editing) return;
    const email = prepForm.email.trim().toLowerCase();
    if (!/^[^@]+@[^@]+\.[^@]+$/.test(email)) { setToast('Email non valida'); return; }
    if (prepForm.password.length < 6) { setToast('Password troppo corta (min 6 caratteri)'); return; }
    setPrepBusy(true);
    try {
      // La destinazione dev'essere sul server (FK di preposto_links). Se appena
      // creata e non ancora sincronizzata, la confermiamo prima.
      const conf = await getSyncEngine().confirmMasterPushed('destinations', editing.id);
      if (!conf.confirmed) {
        setToast(conf.reason === 'offline'
          ? 'Sei offline: serve connessione per creare l’account'
          : 'Attendo la sincronizzazione della destinazione… riprova tra poco');
        return;
      }
      const { error } = await getSupabase().functions.invoke('provision-preposto', {
        body: { destination_id: editing.id, email, password: prepForm.password, display_name: `Preposto ${editing.name}` },
      });
      if (error) {
        let code = '';
        try { code = ((await (error as { context?: Response }).context?.json()) as { error?: string })?.error ?? ''; } catch { /* noop */ }
        setToast(
          code === 'already_registered' ? 'Esiste già un account con questa email'
          : code === 'weak_password' ? 'Password troppo debole'
          : code === 'insufficient_role' || code === 'not_in_tenant' ? 'Non hai i permessi per creare l’account'
          : 'Creazione account non riuscita',
        );
        return;
      }
      setPrepResult({ email, password: prepForm.password });
      setPrepFormOpen(false);
      void qc.invalidateQueries({ queryKey: ['tenant_accounts'] });
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPrepBusy(false);
    }
  }

  // T-438 — reimposta la password del preposto già collegato alla destinazione.
  async function resetPreposto(): Promise<void> {
    if (!editing || resetPw == null) return;
    if (resetPw.length < 6) { setToast('Password troppo corta (min 6 caratteri)'); return; }
    setPrepBusy(true);
    try {
      const { error } = await getSupabase().functions.invoke('provision-preposto', {
        body: { destination_id: editing.id, password: resetPw, reset: true },
      });
      if (error) { setToast('Reset password non riuscito'); return; }
      setPrepResult({ email: accountByDest.get(editing.id) ?? '', password: resetPw });
      setResetPw(null);
      setToast('Password reimpostata ✓');
    } catch (err) {
      setToast(toUserMessage(err));
    } finally {
      setPrepBusy(false);
    }
  }

  async function shareCredentials(): Promise<void> {
    if (!prepResult || !editing) return;
    const text = `Accesso TrackEAT — ${editing.name}\nApp: https://app.trackeat.eu\nEmail: ${prepResult.email}\nPassword: ${prepResult.password}`;
    if (typeof navigator.share === 'function') {
      try { await shareWithNavigator({ title: 'Accesso TrackEAT', text }); return; } catch { /* fallback sotto */ }
    }
    try { await navigator.clipboard.writeText(text); setToast('Credenziali copiate'); }
    catch { setToast('Copia non riuscita'); }
  }

  // T-438 — PERSISTE i reparti (token+pin) SUBITO sul server. Il QR deve essere
  // sul server per la pagina pubblica: senza questo salvataggio immediato il QR
  // stampato porta a "link scaduto" (bug 2026-07-08). Non chiude il modale.
  async function persistWards(newWards: DestinationWard[]): Promise<void> {
    if (!editing) { setToast('Salva prima la destinazione, poi genera i QR'); return; }
    const clean = newWards
      .map((w, i) => ({ id: w.id, name: w.name.trim(), sort_order: i, token: w.token ?? null, pin: w.pin ?? null }))
      .filter((w) => w.name.length > 0);
    // Nomi reparto UNIVOCI (le righe ordine sono taggate col nome): blocca il salvataggio
    // se un rename ha creato un omonimo, altrimenti i due QR leggerebbero lo stesso ordine.
    const lower = clean.map((w) => w.name.toLowerCase());
    const dupName = lower.find((n, i) => lower.indexOf(n) !== i);
    if (dupName) { setToast(`Due reparti hanno lo stesso nome (“${clean[lower.indexOf(dupName)].name}”). Rendili univoci prima di generare i QR.`); return; }
    try {
      // UPSERT (LWW) e NON update: l'update fa OCC su base_version → se la
      // versione locale è in drift (es. dopo generazioni fallite) il server
      // RIGETTA con version_conflict e il token va perso (bug "link scaduto"
      // 2026-07-08). L'upsert applica sempre i valori (sync-push branch upsert).
      await enqueue.mutateAsync({
        table: 'destinations', id: editing.id, op: 'upsert',
        base_version: editing.version ?? 0,
        patch: { id: editing.id, tenant_id: editing.tenant_id, name: editing.name, wards: clean },
      });
      // Assicura che il token sia DAVVERO sul server prima di stampare il QR.
      const conf = await getSyncEngine().confirmMasterPushed('destinations', editing.id).catch(() => ({ confirmed: false, reason: 'error' as const }));
      // Riallinea la versione locale a quella del server (l'upsert l'ha bumpata):
      // così un successivo "Salva modifiche" non va in conflitto.
      try {
        const { data } = await getSupabase().from('destinations').select('version').eq('id', editing.id).maybeSingle();
        const v = (data as { version?: number } | null)?.version;
        setEditing((e) => (e ? ({ ...e, version: typeof v === 'number' ? v : (e.version ?? 0) + 1, wards: clean } as DestinationRow) : e));
      } catch {
        setEditing((e) => (e ? ({ ...e, wards: clean } as DestinationRow) : e));
      }
      if (!conf.confirmed) setToast('QR salvato in locale — si sincronizza appena c’è rete');
    } catch (err) {
      setToast(toUserMessage(err));
    }
  }
  async function ensureWardAccess(idx: number): Promise<void> {
    const newWards = form.wards.map((x, i) => (i === idx ? { ...x, token: x.token || genWardToken(), pin: x.pin || genWardPin() } : x));
    setForm((f) => ({ ...f, wards: newWards }));
    await persistWards(newWards);
    setToast('QR generato e salvato ✓ — ora puoi stamparlo');
  }
  async function regenWardToken(idx: number): Promise<void> {
    const newWards = form.wards.map((x, i) => (i === idx ? { ...x, token: genWardToken() } : x));
    setForm((f) => ({ ...f, wards: newWards }));
    await persistWards(newWards);
    setToast('QR rigenerato e salvato ✓ (il vecchio QR non funziona più)');
  }
  function setWardPin(idx: number, pin: string): void {
    const clean = pin.replace(/\D/g, '').slice(0, 6);
    setForm((f) => ({ ...f, wards: f.wards.map((x, i) => (i === idx ? { ...x, pin: clean } : x)) }));
  }
  // Persiste il PIN al blur (altrimenti un PIN cambiato dopo la generazione non
  // arriverebbe al server e la pagina pubblica validerebbe contro il vecchio).
  async function persistWardPin(idx: number): Promise<void> {
    const w = form.wards[idx];
    if (w?.token) await persistWards(form.wards);
  }

  // Stampa un A4 con QR + PIN + mini-guida per il reparto.
  function printWardQr(w: DestinationWard): void {
    const tok = w.token ?? '';
    const png = tok ? qrPngs[tok] : '';
    if (!tok || !png) { setToast('Genera prima il QR e salva la destinazione.'); return; }
    const dest = editing?.name ?? form.name ?? '';
    const esc = (s: string): string => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
    const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"><title>QR ordine ${esc(w.name)}</title>
      <style>*{box-sizing:border-box}body{font-family:-apple-system,Segoe UI,Roboto,Helvetica,Arial,sans-serif;color:#12333a;margin:0;padding:34px 40px;text-align:center}
      .k{font-size:14px;letter-spacing:.14em;text-transform:uppercase;color:#0b6b63;font-weight:700}
      h1{font-size:40px;margin:6px 0 2px} .sub{font-size:17px;color:#475569;margin:0 0 18px}
      .qr{width:340px;height:340px;margin:6px auto} .qr img{width:100%;height:100%}
      .pin{display:inline-block;margin:12px auto 0;border:2px dashed #0b6b63;border-radius:14px;padding:10px 26px;font-size:26px;font-weight:800;letter-spacing:.22em;color:#0b6b63}
      .pin small{display:block;font-size:12px;letter-spacing:.12em;color:#64748b;font-weight:700}
      ol{max-width:520px;margin:22px auto 0;text-align:left;font-size:16px;line-height:1.7} li{margin-bottom:4px}
      .foot{margin-top:26px;font-size:12px;color:#94a3b8}
      @media print{body{padding:12px}.noprint{display:none}}
      .noprint{margin-top:22px}.noprint button{font-size:15px;padding:10px 22px;border:none;border-radius:8px;background:#0b6b63;color:#fff;cursor:pointer}</style></head><body>
      <div class="k">${esc(dest)}</div>
      <h1>Reparto ${esc(w.name)}</h1>
      <div class="sub">Ordine pasti del giorno — inquadra il QR con il telefono</div>
      <div class="qr"><img src="${png}" alt="QR"></div>
      <div class="pin">${esc(w.pin ?? '----')}<small>PIN reparto</small></div>
      <ol>
        <li>Inquadra il <b>QR</b> con la fotocamera del telefono e apri il link.</li>
        <li>Inserisci il <b>PIN</b> qui sopra.</li>
        <li>Aggiungi un <b>letto</b>, scegli la <b>dieta</b> del paziente e le eventuali <b>note</b> (allergie).</li>
        <li>Ripeti per ogni letto, poi <b>salva</b>: l'ordine arriva al preposto per il controllo.</li>
      </ol>
      <div class="foot">TrackEAT · Non serve installare nessuna app · ${esc(wardOrderUrl(tok))}</div>
      </body></html>`;
    // Mostra il foglio in un modale IN-APP (iframe) → sempre chiudibile, stampabile.
    setPrintHtml(html);
  }
  function doPrintWard(): void {
    try {
      const win = printRef.current?.contentWindow;
      win?.focus();
      win?.print();
    } catch { setToast('Stampa non disponibile su questo dispositivo — usa uno screenshot'); }
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            {/* T-403: si arriva dal tile della Home, non più da Impostazioni. */}
            <IonBackButton defaultHref={isTransportRecipients ? '/documents/transport' : '/today'} />
          </IonButtons>
          <IonTitle>
            {isTransportRecipients
              ? 'Clienti e destinatari'
              : (zoneName ? `Destinazioni — ${zoneName}` : 'Destinazioni')}
          </IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent style={{ '--background': '#eef2f7' } as React.CSSProperties}>
        {!isTransportRecipients && !collettivaEnabled && (
          <div style={{ padding: '24px 16px' }}>
            <p style={{ fontSize: 14, color: '#475569' }}>
              Modulo collettiva non attivo per questo locale. L&apos;anagrafica
              destinazioni serve solo alla ristorazione collettiva (pasti
              veicolati a scuole/ospedali).
            </p>
          </div>
        )}
        {(isTransportRecipients || collettivaEnabled) && (
        <div style={{ padding: '12px 16px 96px' }}>
          <p style={{ fontSize: 13, color: '#475569', margin: '4px 4px 12px' }}>
            {isTransportRecipients
              ? 'Clienti e destinatari esterni usati nei DDT di vendita. L’anagrafica è condivisa tra tutti i ristoranti dell’azienda.'
              : 'I punti di consegna dei pasti veicolati (scuole, ospedali, RSA…). Servono per emettere i documenti di trasporto in uscita.'}
          </p>

          <IonButton expand="block" onClick={() => openNew()} style={{ marginBottom: 16 }}>
            <IonIcon icon={add} slot="start" />
            {isTransportRecipients ? 'Nuovo cliente/destinatario' : 'Nuova destinazione'}
          </IonButton>

          <h2 style={{ fontSize: 14, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '4px 4px 12px' }}>
            {isTransportRecipients
              ? `Clienti/destinatari attivi · ${visible.length}`
              : `Destinazioni attive${zoneName ? ` — zona ${zoneName}` : ''} · ${visible.length}`}
          </h2>

          {visible.length === 0 ? (
            <div style={{ background: '#fff', borderRadius: 14, padding: 16, color: '#64748b', fontSize: 13 }}>
              <p style={{ margin: 0, fontStyle: 'italic' }}>
                {isTransportRecipients ? 'Nessun cliente/destinatario registrato.' : 'Nessuna destinazione registrata.'}
              </p>
              <p style={{ margin: '8px 0 0' }}>
                {isTransportRecipients
                  ? 'Aggiungi il cliente o destinatario esterno da selezionare nei DDT di vendita.'
                  : 'Aggiungi qui le scuole, gli ospedali e le altre strutture a cui consegni i pasti: ogni DDT in uscita dovrà indicare una di queste destinazioni.'}
              </p>
            </div>
          ) : (
            visible.map((d) => {
              const badge = KIND_BADGE_COLORS[d.kind];
              const subtitle = [d.city, d.contact_name].filter(Boolean).join(' · ');
              return (
                <div
                  key={d.id}
                  onClick={() => openEdit(d)}
                  role="button"
                  style={{
                    background: '#fff', borderRadius: 14, padding: '14px 16px', marginBottom: 10,
                    boxShadow: '0 1px 2px rgba(15,23,42,.06), 0 2px 6px rgba(15,23,42,.05)',
                    display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: 40, height: 40, borderRadius: 20, background: badge.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <IonIcon icon={businessOutline} style={{ fontSize: 20, color: badge.fg }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>{d.name}</span>
                      <span style={{
                        fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em',
                        background: badge.bg, color: badge.fg, borderRadius: 999, padding: '2px 8px',
                      }}>
                        {DESTINATION_KIND_LABELS[d.kind]}
                      </span>
                      {!isTransportRecipients && accountByDest.has(d.id) && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#15803d', background: '#dcfce7', borderRadius: 999, padding: '2px 8px' }}>
                          🔑 accesso
                        </span>
                      )}
                    </div>
                    {subtitle && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{subtitle}</div>
                    )}
                  </div>
                  <IonButton
                    fill="clear"
                    size="small"
                    color="danger"
                    onClick={(e) => { e.stopPropagation(); setConfirmArchive(d); }}
                    aria-label={isTransportRecipients ? 'Archivia cliente/destinatario' : 'Archivia destinazione'}
                  >
                    <IonIcon icon={trashOutline} slot="icon-only" />
                  </IonButton>
                </div>
              );
            })
          )}
        </div>
        )}

        <IonModal isOpen={editorOpen} onDidDismiss={() => setEditorOpen(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>
                {isTransportRecipients
                  ? (editing ? 'Modifica cliente/destinatario' : 'Nuovo cliente/destinatario')
                  : (editing ? 'Modifica destinazione' : 'Nuova destinazione')}
              </IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setEditorOpen(false)} aria-label="chiudi">
                  <IonIcon icon={closeOutline} />
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonItem>
              <IonLabel position="stacked">Nome *</IonLabel>
              <IonInput
                value={form.name}
                placeholder={isTransportRecipients ? 'Es. Cliente Rossi S.r.l.' : 'Es. Scuola primaria G. Verdi'}
                onIonInput={(ev) => setForm((f) => ({ ...f, name: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Tipo *</IonLabel>
              <IonSelect
                value={form.kind}
                onIonChange={(ev) => setForm((f) => ({ ...f, kind: ev.detail.value as DestinationKind }))}
              >
                {DESTINATION_KINDS.map((k) => (
                  <IonSelectOption key={k} value={k}>{DESTINATION_KIND_LABELS[k]}</IonSelectOption>
                ))}
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Indirizzo</IonLabel>
              <IonInput
                value={form.address}
                placeholder="Via e numero civico"
                onIonInput={(ev) => setForm((f) => ({ ...f, address: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Città</IonLabel>
              <IonInput
                value={form.city}
                placeholder="Es. Agrigento"
                onIonInput={(ev) => setForm((f) => ({ ...f, city: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Telefono</IonLabel>
              <IonInput
                type="tel"
                inputmode="tel"
                value={form.phone}
                placeholder="Es. 0922 123456"
                onIonInput={(ev) => setForm((f) => ({ ...f, phone: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Email</IonLabel>
              <IonInput
                type="email"
                inputmode="email"
                autocapitalize="off"
                value={form.email}
                placeholder="segreteria@esempio.it"
                onIonInput={(ev) => setForm((f) => ({ ...f, email: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Referente</IonLabel>
              <IonInput
                value={form.contact_name}
                placeholder="Nome e cognome"
                onIonInput={(ev) => setForm((f) => ({ ...f, contact_name: String(ev.detail.value ?? '') }))}
              />
              <IonNote style={{ fontSize: 11, paddingBottom: 6 }}>
                Referente con potere decisionale per ritiri urgenti
              </IonNote>
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">Note</IonLabel>
              <IonTextarea
                value={form.notes}
                autoGrow
                rows={2}
                placeholder="Es. consegna entro le 11:30, ingresso laterale"
                onIonInput={(ev) => setForm((f) => ({ ...f, notes: String(ev.detail.value ?? '') }))}
              />
            </IonItem>
            {/* T-421: REPARTI opzionali della destinazione */}
            {!isTransportRecipients && (
              <div style={{ marginTop: 22, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
              <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 4px' }}>
                Reparti (opzionale)
              </h2>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>
                Se questa destinazione ha reparti (es. SPDC, Ortopedia, Rianimazione…),
                il preposto potrà inserire le quantità reparto per reparto. Lascia
                vuoto per ordinare a livello destinazione.
              </p>
              {form.wards.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 10 }}>
                  {form.wards.map((w, idx) => {
                    const tok = w.token ?? '';
                    const png = tok ? qrPngs[tok] : '';
                    return (
                    <div key={w.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '8px 10px 12px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <IonInput
                          value={w.name}
                          placeholder="Nome reparto"
                          style={{ flex: 1 }}
                          onIonInput={(ev) => {
                            // '|' è il separatore delle chiavi quantità lato preposto → vietato nei nomi.
                            const v = String(ev.detail.value ?? '').replace(/\|/g, '/');
                            setForm((f) => ({ ...f, wards: f.wards.map((x, i) => (i === idx ? { ...x, name: v } : x)) }));
                          }}
                        />
                        <IonButton fill="clear" size="small" color="danger" aria-label="rimuovi reparto"
                          onClick={() => setForm((f) => ({ ...f, wards: f.wards.filter((_, i) => i !== idx) }))}>
                          <IonIcon icon={removeCircleOutline} slot="icon-only" />
                        </IonButton>
                      </div>
                      {/* T-438: accesso QR + PIN del reparto */}
                      {!tok ? (
                        <IonButton size="small" fill="outline" style={{ marginTop: 4 }} onClick={() => void ensureWardAccess(idx)}>
                          <IonIcon icon={qrCodeOutline} slot="start" /> Genera QR + PIN
                        </IonButton>
                      ) : (
                        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                          <div style={{ width: 78, height: 78, border: '1px solid #e2e8f0', borderRadius: 8, background: '#fff', flex: '0 0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {png ? <img src={png} alt="QR" style={{ width: 74, height: 74 }} /> : <IonSpinner name="dots" />}
                          </div>
                          <div style={{ flex: 1, minWidth: 140 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#475569' }}>PIN</span>
                              <IonInput
                                value={w.pin ?? ''}
                                inputmode="numeric"
                                placeholder="1234"
                                style={{ maxWidth: 90, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 800, letterSpacing: '0.2em', '--padding-start': '10px' } as React.CSSProperties}
                                onIonInput={(ev) => setWardPin(idx, String(ev.detail.value ?? ''))}
                                onIonBlur={() => void persistWardPin(idx)}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                              <IonButton size="small" onClick={() => printWardQr(w)}>
                                <IonIcon icon={printOutline} slot="start" /> Stampa A4
                              </IonButton>
                              <IonButton size="small" fill="clear" color="medium" onClick={() => void regenWardToken(idx)}>
                                Rigenera QR
                              </IonButton>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>
              )}
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 8px' }}>
                «Genera QR + PIN» <strong>salva subito</strong> il reparto sul server → il QR è già valido: stampa l'A4 e
                lascialo in reparto. Scansionandolo si apre l'ordine per letto di quel reparto (serve il PIN, niente login).
              </p>
              <div style={{ display: 'flex', gap: 8 }}>
                <IonInput
                  value={newWard}
                  placeholder="Aggiungi reparto (es. SPDC)"
                  style={{ flex: 1, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, '--padding-start': '12px' } as React.CSSProperties}
                  onIonInput={(ev) => setNewWard(String(ev.detail.value ?? ''))}
                />
                <IonButton
                  onClick={() => {
                    const n = newWard.trim().replace(/\|/g, '/');
                    if (!n) return;
                    // Nomi reparto UNIVOCI per destinazione: le righe ordine sono taggate
                    // col NOME del reparto, quindi due reparti omonimi si leggerebbero /
                    // sovrascriverebbero gli ordini a vicenda. Blocca il duplicato.
                    if (form.wards.some((x) => x.name.trim().toLowerCase() === n.toLowerCase())) {
                      setToast(`Esiste già un reparto “${n}”. Usa un nome diverso.`);
                      return;
                    }
                    setForm((f) => ({ ...f, wards: [...f.wards, { id: crypto.randomUUID(), name: n, sort_order: f.wards.length }] }));
                    setNewWard('');
                  }}
                >
                  <IonIcon icon={add} slot="icon-only" />
                </IonButton>
              </div>
              </div>
            )}

            {/* T-421: MODELLO ordine = sottoinsieme di righe del catalogo */}
            {!isTransportRecipients && (
              <div style={{ marginTop: 22, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
              <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 4px' }}>
                Modello ordine (righe visibili al preposto)
              </h2>
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 10px' }}>
                {form.orderModel == null
                  ? 'Nessun modello: il preposto vede TUTTE le righe del catalogo. Seleziona alcune righe per limitare a un modello dedicato a questa destinazione.'
                  : `Modello attivo: ${form.orderModel.length} righe selezionate. Il preposto vedrà solo queste.`}
              </p>
              {form.orderModel != null && (
                <IonButton fill="clear" size="small" style={{ marginBottom: 4 }}
                  onClick={() => setForm((f) => ({ ...f, orderModel: null }))}>
                  Azzera modello (mostra tutto)
                </IonButton>
              )}
              {catalog.length === 0 ? (
                <IonNote style={{ fontSize: 12 }}>Catalogo ordini non ancora disponibile.</IonNote>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[...CATALOG_CAT_ORDER.filter((c) => catalogByCat.has(c)), ...[...catalogByCat.keys()].filter((c) => !CATALOG_CAT_ORDER.includes(c))].map((cat) => (
                    <div key={cat}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 2px 4px' }}>{cat}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(catalogByCat.get(cat) ?? []).map((r) => {
                          const sel = form.orderModel == null || form.orderModel.includes(r.label);
                          return (
                            <button
                              key={r.label}
                              type="button"
                              onClick={() => setForm((f) => {
                                // Prima selezione da "tutto" → parti dall'intero catalogo e togli questa.
                                const base = f.orderModel ?? catalog.map((x) => x.label);
                                const next = base.includes(r.label) ? base.filter((l) => l !== r.label) : [...base, r.label];
                                return { ...f, orderModel: next };
                              })}
                              style={{
                                fontSize: 12, padding: '5px 10px', borderRadius: 999, cursor: 'pointer',
                                border: `1px solid ${sel ? '#2563eb' : '#cbd5e1'}`,
                                background: sel ? '#eff6ff' : '#fff',
                                color: sel ? '#1d4ed8' : '#64748b', fontWeight: sel ? 600 : 400,
                              }}
                            >
                              {sel ? '✓ ' : ''}{r.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            )}

            <IonButton expand="block" style={{ marginTop: 16 }} onClick={() => { void onSave(); }}>
              {editing
                ? 'Salva modifiche'
                : (isTransportRecipients ? 'Crea cliente/destinatario' : 'Crea destinazione')}
            </IonButton>

            {!isTransportRecipients && editing && isAdmin && (
              <div style={{ marginTop: 24, borderTop: '1px solid #e2e8f0', paddingTop: 18 }}>
                <h2 style={{ fontSize: 13, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.04em', margin: '0 0 6px' }}>
                  Account di accesso
                </h2>
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 12px' }}>
                  Un accesso dedicato a questa destinazione: chi lo usa vede e invia
                  solo i propri ordini, niente altro del locale.
                </p>

                {accountByDest.has(editing.id) && !prepResult ? (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <IonIcon icon={keyOutline} style={{ color: '#15803d', fontSize: 18 }} />
                      <div style={{ fontSize: 13, color: '#166534' }}>
                        Account attivo: <strong style={{ wordBreak: 'break-all' }}>{accountByDest.get(editing.id)}</strong>
                      </div>
                    </div>
                    {resetPw == null ? (
                      <IonButton size="small" fill="clear" style={{ marginTop: 4 }} onClick={() => setResetPw(genPassword())}>
                        <IonIcon icon={keyOutline} slot="start" /> Reimposta password
                      </IonButton>
                    ) : (
                      <div style={{ marginTop: 8 }}>
                        <IonItem>
                          <IonLabel position="stacked">Nuova password *</IonLabel>
                          <IonInput value={resetPw} onIonInput={(ev) => setResetPw(String(ev.detail.value ?? ''))} />
                        </IonItem>
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                          <IonButton size="small" style={{ flex: 1 }} disabled={prepBusy} onClick={() => { void resetPreposto(); }}>
                            {prepBusy ? 'Salvo…' : 'Conferma nuova password'}
                          </IonButton>
                          <IonButton size="small" fill="clear" disabled={prepBusy} onClick={() => setResetPw(null)}>Annulla</IonButton>
                        </div>
                      </div>
                    )}
                  </div>
                ) : prepResult ? (
                  <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 8 }}>✓ Account creato — condividi le credenziali</div>
                    <div style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, background: '#fff', borderRadius: 8, padding: 10, color: '#0f172a', wordBreak: 'break-all' }}>
                      Email: {prepResult.email}<br />Password: {prepResult.password}
                    </div>
                    <IonButton expand="block" style={{ marginTop: 10 }} onClick={() => { void shareCredentials(); }}>
                      <IonIcon icon={copyOutline} slot="start" />
                      Copia / condividi credenziali
                    </IonButton>
                  </div>
                ) : prepFormOpen ? (
                  <div>
                    <IonItem>
                      <IonLabel position="stacked">Email accesso *</IonLabel>
                      <IonInput
                        type="email"
                        inputmode="email"
                        autocapitalize="off"
                        value={prepForm.email}
                        placeholder="referente@struttura.it"
                        onIonInput={(ev) => setPrepForm((f) => ({ ...f, email: String(ev.detail.value ?? '') }))}
                      />
                    </IonItem>
                    <IonItem>
                      <IonLabel position="stacked">Password *</IonLabel>
                      <IonInput
                        value={prepForm.password}
                        onIonInput={(ev) => setPrepForm((f) => ({ ...f, password: String(ev.detail.value ?? '') }))}
                      />
                      <IonNote style={{ fontSize: 11, paddingBottom: 6 }}>
                        Generata in automatico — puoi cambiarla. La condividerai col referente.
                      </IonNote>
                    </IonItem>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <IonButton expand="block" style={{ flex: 1 }} disabled={prepBusy} onClick={() => { void createPreposto(); }}>
                        {prepBusy ? 'Creo…' : 'Crea account'}
                      </IonButton>
                      <IonButton fill="clear" disabled={prepBusy} onClick={() => setPrepFormOpen(false)}>
                        Annulla
                      </IonButton>
                    </div>
                  </div>
                ) : (
                  <IonButton
                    expand="block"
                    fill="outline"
                    onClick={() => { setPrepForm({ email: editing.email ?? '', password: genPassword() }); setPrepFormOpen(true); }}
                  >
                    <IonIcon icon={keyOutline} slot="start" />
                    Crea account di accesso
                  </IonButton>
                )}
              </div>
            )}
          </IonContent>
        </IonModal>

        <IonAlert
          isOpen={!!confirmArchive}
          header={isTransportRecipients ? 'Archivia cliente/destinatario' : 'Archivia destinazione'}
          message={confirmArchive
            ? (isTransportRecipients
              ? `Archiviare "${confirmArchive.name}"? Non comparirà più tra i clienti e destinatari selezionabili per i nuovi DDT; i documenti già emessi restano invariati.`
              : `Archiviare "${confirmArchive.name}"? Non comparirà più tra le destinazioni selezionabili per i nuovi documenti; i documenti già emessi restano invariati.`)
            : ''}
          buttons={[
            { text: 'Annulla', role: 'cancel' },
            { text: 'Archivia', role: 'destructive', handler: () => { if (confirmArchive) void archive(confirmArchive); } },
          ]}
          onDidDismiss={() => setConfirmArchive(null)}
        />

        {/* T-438 fix — foglio A4 QR IN-APP (iframe), sempre chiudibile */}
        {!isTransportRecipients && (
          <IonModal isOpen={printHtml !== null} onDidDismiss={() => setPrintHtml(null)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>Foglio QR reparto</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setPrintHtml(null)}>Chiudi</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            <iframe
              ref={printRef}
              srcDoc={printHtml ?? ''}
              title="Foglio QR reparto"
              style={{ width: '100%', height: '100%', border: 'none', background: '#fff' }}
            />
          </IonContent>
          <IonFooter>
            <IonToolbar>
              <div style={{ padding: '6px 12px' }}>
                <IonButton expand="block" onClick={() => doPrintWard()}>🖨️ Stampa</IonButton>
              </div>
            </IonToolbar>
          </IonFooter>
          </IonModal>
        )}

        <IonToast isOpen={!!toast} message={toast ?? ''} duration={1800} onDidDismiss={() => setToast(null)} />
      </IonContent>
    </IonPage>
  );
}
